/**
 * Design tokens — единственный источник правды для всей админки.
 *
 *  - AntD ThemeConfig генерится отсюда (`antd-theme.ts → buildAntdTheme`).
 *  - CSS-переменные пишутся отсюда из JS в `<html>` через
 *    `ThemeModeProvider` (см. `components/ThemeModeProvider/`). Этим
 *    же путём поверх базы накладывается user-accent.
 *  - Кастомизация по доменам запрещена: меняем тут — меняется везде.
 */

export type ThemeMode = 'light' | 'dark';

/* ── Цветовые шкалы (brand + neutral) ──────────────────────────── */

const blue = {
  50: '#EFF6FF',
  100: '#DBEAFE',
  200: '#BFDBFE',
  300: '#93C5FD',
  400: '#60A5FA',
  500: '#3B82F6',
  600: '#2563EB',
  700: '#1D4ED8',
  800: '#1E40AF',
  900: '#1E3A8A',
};

const coral = {
  50: '#FFF0F2',
  100: '#FFD9DE',
  200: '#FFB2BC',
  300: '#FF8A99',
  400: '#FF6F81',
  500: '#F5455C',
  600: '#D8304A',
  700: '#AB2138',
  800: '#7E1628',
  900: '#500D18',
};

const mint = {
  50: '#E8FBF3',
  100: '#C8F4DF',
  200: '#8DE6BE',
  300: '#56D69E',
  400: '#2EC788',
  500: '#16B981',
  600: '#0FA370',
  700: '#0B7F56',
  800: '#08593C',
  900: '#053825',
};

const amber = {
  50: '#FFF7E6',
  100: '#FFEBBD',
  200: '#FFD87A',
  300: '#FFC44D',
  400: '#FFB547',
  500: '#F7A11A',
  600: '#DA8810',
  700: '#A9680A',
  800: '#754707',
  900: '#462B04',
};

const violet = {
  50: '#F1EFFE',
  100: '#E5E1FD',
  200: '#CCC4FA',
  300: '#AEA1F3',
  400: '#9281EC',
  500: '#7B5CE6',
  600: '#6442D4',
  700: '#5232AB',
  800: '#3A2378',
  900: '#241646',
};

const neutral = {
  0: '#FFFFFF',
  50: '#F8F9FC',
  100: '#EFF1F7',
  150: '#E8E9F0',
  200: '#D8DAE6',
  300: '#B7BACB',
  400: '#8A8FA6',
  500: '#6C718A',
  600: '#5B6073',
  700: '#40455C',
  800: '#272B40',
  850: '#1A1C2B',
  900: '#14162A',
  950: '#0E0F1A',
  1000: '#08091A',
};

/* ── Семантические токены (light) ──────────────────────────────── */

const light = {
  mode: 'light' as ThemeMode,
  color: {
    brand: blue[500],
    brandHover: blue[600],
    brandActive: blue[700],
    brandSoft: blue[50],
    brandBorder: blue[200],
    brandText: blue[700],

    accent: coral[500],
    accentSoft: coral[50],
    accentText: coral[700],

    success: mint[500],
    successSoft: mint[50],
    successText: mint[700],

    warning: amber[500],
    warningSoft: amber[50],
    warningText: amber[700],

    error: coral[500],
    errorSoft: coral[50],
    errorText: coral[700],

    info: blue[500],
    infoSoft: blue[50],
    infoText: blue[700],

    // Партнёрская продажа — фиолетовый, отдельно от обычных статусов.
    partner: violet[500],
    partnerSoft: violet[50],
    partnerText: violet[700],

    bg: neutral[50],
    bgElevated: neutral[0],
    bgSunken: neutral[100],

    surface: neutral[0],
    surfaceHover: neutral[50],
    surfaceActive: neutral[100],

    border: neutral[150],
    borderStrong: neutral[200],
    borderSubtle: neutral[100],

    textPrimary: neutral[900],
    textSecondary: neutral[600],
    textTertiary: neutral[400],
    textDisabled: neutral[300],
    textOnBrand: neutral[0],

    // Сайдбар тёмный в обеих темах — часть фирменного стиля.
    siderBg: neutral[950],
    siderBgElevated: neutral[900],
    siderText: neutral[200],
    siderTextMuted: neutral[400],
    siderItemActive: blue[500],
    siderBorder: neutral[850],
  },
};

/* ── Семантические токены (dark) ───────────────────────────────── */

const dark = {
  mode: 'dark' as ThemeMode,
  color: {
    brand: blue[400],
    brandHover: blue[300],
    brandActive: blue[500],
    brandSoft: 'rgba(59, 130, 246, 0.14)',
    brandBorder: blue[700],
    brandText: blue[300],

    accent: coral[400],
    accentSoft: 'rgba(245, 69, 92, 0.14)',
    accentText: coral[300],

    success: mint[400],
    successSoft: 'rgba(22, 185, 129, 0.14)',
    successText: mint[300],

    warning: amber[300],
    warningSoft: 'rgba(247, 161, 26, 0.14)',
    warningText: amber[200],

    error: coral[400],
    errorSoft: 'rgba(245, 69, 92, 0.14)',
    errorText: coral[300],

    info: blue[400],
    infoSoft: 'rgba(59, 130, 246, 0.14)',
    infoText: blue[300],

    partner: violet[400],
    partnerSoft: 'rgba(123, 92, 230, 0.16)',
    partnerText: violet[300],

    bg: neutral[950],
    bgElevated: neutral[900],
    bgSunken: neutral[1000],

    surface: neutral[850],
    surfaceHover: neutral[800],
    surfaceActive: neutral[700],

    border: neutral[800],
    borderStrong: neutral[700],
    borderSubtle: neutral[850],

    textPrimary: neutral[50],
    textSecondary: neutral[300],
    textTertiary: neutral[400],
    textDisabled: neutral[600],
    textOnBrand: neutral[0],

    siderBg: neutral[1000],
    siderBgElevated: neutral[900],
    siderText: neutral[100],
    siderTextMuted: neutral[400],
    siderItemActive: blue[400],
    siderBorder: neutral[900],
  },
};

export type SemanticTokens = typeof light;

export const THEME: Record<ThemeMode, SemanticTokens> = {light, dark};

/* ── Mode-независимые ──────────────────────────────────────────── */

export const radius = {
  xs: 4,
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  pill: 999,
};

export const fontFamily = {
  sans: '"Inter Variable", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  mono: 'ui-monospace, "SF Mono", "JetBrains Mono", "Fira Code", Consolas, monospace',
};

export const fontSize = {
  xs: 11,
  sm: 12,
  base: 13,
  md: 14,
  lg: 16,
  xl: 20,
  '2xl': 24,
  '3xl': 32,
  '4xl': 40,
};

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

export const lineHeight = {
  tight: 1.2,
  snug: 1.35,
  normal: 1.5,
  relaxed: 1.65,
};

export const shadow = {
  light: {
    sm: '0 1px 2px rgba(14, 16, 32, 0.06), 0 0 0 1px rgba(14, 16, 32, 0.04)',
    md: '0 4px 12px rgba(14, 16, 32, 0.08), 0 0 0 1px rgba(14, 16, 32, 0.04)',
    lg: '0 12px 28px rgba(34, 17, 103, 0.14), 0 0 0 1px rgba(14, 16, 32, 0.04)',
    focus: '0 0 0 3px rgba(59, 130, 246, 0.25)',
    brand: '0 6px 18px rgba(59, 130, 246, 0.35)',
  },
  dark: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.04)',
    md: '0 4px 14px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.04)',
    lg: '0 16px 36px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.04)',
    focus: '0 0 0 3px rgba(139, 112, 255, 0.35)',
    brand: '0 6px 18px rgba(59, 130, 246, 0.55)',
  },
} as const;

export const layout = {
  // Уменьшили под жалобы тестеров: на десктопе шапка/сайдбар занимали
  // слишком много места, контент не дышал.
  headerHeight: 48,
  siderWidth: 220,
  siderCollapsedWidth: 64,
  contentMaxWidth: 1440,
};

/* ── CSS-переменные ────────────────────────────────────────────── */

const kebab = (s: string): string => s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

// Плоский словарь CSS-переменных для заданного режима.
export const cssVarsFor = (mode: ThemeMode): Record<string, string> => {
  const t = THEME[mode];
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(t.color)) {
    out[`--color-${kebab(k)}`] = v;
  }
  const sh = shadow[mode];
  for (const [k, v] of Object.entries(sh)) out[`--shadow-${k}`] = v;
  for (const [k, v] of Object.entries(radius)) out[`--radius-${k}`] = `${v}px`;
  out['--font-sans'] = fontFamily.sans;
  out['--font-mono'] = fontFamily.mono;
  out['--layout-header-height'] = `${layout.headerHeight}px`;
  out['--layout-sider-width'] = `${layout.siderWidth}px`;
  out['--layout-sider-collapsed-width'] = `${layout.siderCollapsedWidth}px`;
  out['--layout-content-max-width'] = `${layout.contentMaxWidth}px`;
  return out;
};
